import axios from 'axios';

const baseURL = process.env.REACT_APP_DJANGO_API_URL || 'http://localhost:8000/api';
const API = axios.create({ baseURL });
let refreshPromise = null;
class SessionChanged extends Error {}

export const storeLoginSession = (data) => {
  const session = { access_token: data.access_token, refresh_token: data.refresh_token,
                    client_session_id: crypto.randomUUID() };
  localStorage.removeItem('auth_logout_in_progress');
  localStorage.setItem('token', session.access_token);
  localStorage.setItem('session', JSON.stringify(session));
};

const readSession = () => {
  try {
    const session = JSON.parse(localStorage.getItem('session') || '{}');
    return session && typeof session === 'object' && !Array.isArray(session) ? session : {};
  }
  catch { return {}; }
};

export const revokeServerSession = async () => {
  const session = readSession();
  localStorage.setItem('auth_logout_in_progress', session.client_session_id || 'logout');
  const access_token = localStorage.getItem('token') || session.access_token;
  if (!access_token || !session.refresh_token) return;
  try {
    await fetch(`${baseURL}/logout/`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access_token}` }, keepalive: true,
      ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(5000) } : {}),
      body: JSON.stringify({ access_token, refresh_token: session.refresh_token }),
    });
  } catch { /* Local logout still completes if the network is unavailable. */ }
};

export const clearClientSession = async (expectedSession = readSession().client_session_id) => {
  if (readSession().client_session_id !== expectedSession) return false;
  await revokeServerSession();
  // A different account may have signed in while revocation was in flight.
  if (readSession().client_session_id !== expectedSession) return false;
  localStorage.clear();
  sessionStorage.clear();
  if (typeof document !== 'undefined') {
    document.cookie.split(';').filter(Boolean).forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }
  window.dispatchEvent(new Event('auth-state-changed'));
  return true;
};

const hardLogout = async (expectedSession) => {
  if (await clearClientSession(expectedSession) && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshToken = (failedToken, expectedSession) => {
  if (refreshPromise) return refreshPromise;
  const refresh = async () => {
    if (localStorage.getItem('auth_logout_in_progress') || readSession().client_session_id !== expectedSession) {
      throw new SessionChanged();
    }
    const current = localStorage.getItem('token') || readSession().access_token;
    // Another request/tab may already have rotated this session.
    if (current && current !== failedToken) return current;
    const session = readSession();
    if (!session.refresh_token) { const error = new Error('Session expired'); error.reauthenticate = true; throw error; }
    // A bare axios call avoids recursively invoking these interceptors.
    const { data } = await axios.post(`${baseURL}/auth/refresh/`, { refresh_token: session.refresh_token });
    if (!data.access_token || !data.refresh_token) throw new Error('Invalid session response');
    if (localStorage.getItem('auth_logout_in_progress') || readSession().client_session_id !== expectedSession
        || readSession().refresh_token !== session.refresh_token) throw new SessionChanged();
    localStorage.setItem('session', JSON.stringify({ ...data, client_session_id: expectedSession }));
    localStorage.setItem('token', data.access_token);
    window.dispatchEvent(new Event('auth-state-changed'));
    return data.access_token;
  };
  const locks = typeof navigator !== 'undefined' && navigator.locks;
  refreshPromise = (locks ? locks.request('llminate-session-refresh', refresh) : refresh())
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

API.interceptors.request.use((config) => {
  if (!config.skipAuth) {
    const session = readSession();
    if (!session.client_session_id && session.refresh_token) {
      session.client_session_id = crypto.randomUUID();
      localStorage.setItem('session', JSON.stringify(session));
    }
    const token = localStorage.getItem('token') || session.access_token;
    config._authSession = config._authSession || session.client_session_id;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(response => response, async error => {
  const request = error.config;
  if (error.response?.status === 401 && request && !request.skipAuth && !request._retry) {
    request._retry = true;
    try {
      const failedToken = String(request.headers?.Authorization || '').replace(/^Bearer /, '');
      const token = await refreshToken(failedToken, request._authSession);
      request.headers.Authorization = `Bearer ${token}`;
      return API(request);
    } catch (refreshError) {
      // A transport outage preserves the session for a later retry.
      // A changed session cancels stale work without logging out a new user.
      if (!(refreshError instanceof SessionChanged) && readSession().client_session_id === request._authSession
          && !localStorage.getItem('auth_logout_in_progress')
          && (refreshError.reauthenticate || refreshError.response?.status === 401)) {
        await hardLogout(request._authSession);
      }
    }
  }
  return Promise.reject(error);
});

export default API;
